// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BiSearch } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useGetOrgUsersQuery } from '../queries/getOrgUsers.query.generated';
import { useGetOrgUsersCountQuery } from '../queries/getOrgUsersCount.query.generated';
import { useUpsertAllowedCustomersMutation } from '../queries/upsertAllowedCustomers.mutation.generated';
import { useRemoveAllowedCustomerMutation } from '../queries/removeAllowedCustomer.mutation.generated';
import { useSaveUserDetailsMutation } from '../queries/saveUserDetails.mutation.generated';
import type { ScopedCustomer } from '../hooks/useCustomerScope';
import { useUser } from '@store';
import { useDebounce } from '@hooks/useDebounce';
import UserCard from './UserCard';
import RoleChip from './RoleChip';
import { buildScopedUsersWhere } from '../utils/whereClauseBuilders';
import { calculateTargetRole, getRoleLabel, hasDriverRole } from '../utils/roleUtils';
import { buildCustomerUpsertObject } from '../utils/mutationPayloadUtils';
import { useRoleSwap } from '../hooks/useRoleSwap';
import { useCanManageUserAccess } from '../hooks/useCanManageUserAccess';

// TYPES ---------------------------------------------------------------- //

interface CustomerAccessPanelProps {
  customerIds: number[];
  customers: ScopedCustomer[];
}

interface OrgUser {
  id: number;
  display_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  default_role: string;
  active: boolean;
  customer_id: number | null;
  users_usertocustomers: {
    customer_id: number;
    customer: { id: number; name: string };
  }[];
  driver: { id: number; status: string } | null;
}

type SortOrder = 'asc' | 'desc';

// COMPONENT ---------------------------------------------------------------- //

const CustomerAccessPanel: React.FC<CustomerAccessPanelProps> = ({ customerIds, customers }) => {
  const theme = useTheme();
  const currentUser = useUser();
  const currentUserEmail = currentUser?.profile?.email || '';
  const currentUserId = currentUser?.profile?.id;
  const canManageUser = useCanManageUserAccess();
  const [upsertCustomers] = useUpsertAllowedCustomersMutation();
  const [removeCustomer] = useRemoveAllowedCustomerMutation();
  const [updateUser] = useSaveUserDetailsMutation();
  const { syncRoles } = useRoleSwap();

  // Market selection
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);

  // User table state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('display_name');
  const [order, setOrder] = useState<SortOrder>('asc');

  const debouncedSearch = useDebounce(search.trim(), 300);
  useEffect(() => { setPage(0); }, [debouncedSearch]);

  // Auto-select first rooftop
  useEffect(() => {
    if (customers.length > 0 && selectedCustomerId === null) {
      setSelectedCustomerId(customers[0].id);
    }
  }, [customers, selectedCustomerId]);

  // Users query
  const where = useMemo(
    () => buildScopedUsersWhere(customerIds, { search: debouncedSearch || undefined, showInactive: true }),
    [customerIds, debouncedSearch],
  );

  const gqlOrderBy = useMemo(() => [{ [orderBy]: order }], [orderBy, order]);

  const { data, loading, previousData, refetch } = useGetOrgUsersQuery({
    variables: { where, orderBy: gqlOrderBy, limit: rowsPerPage, offset: page * rowsPerPage },
    skip: customerIds.length === 0 || selectedCustomerId === null,
    fetchPolicy: 'cache-and-network',
  });
  const displayData = data ?? previousData;

  const { data: countData } = useGetOrgUsersCountQuery({
    variables: { where },
    skip: customerIds.length === 0 || selectedCustomerId === null,
    fetchPolicy: 'cache-and-network',
  });

  const users: OrgUser[] = (displayData?.users as OrgUser[]) ?? [];
  const totalCount = countData?.users_aggregate?.aggregate?.count ?? 0;

  // Optimistic state
  const [optimistic, setOptimistic] = useState<Map<number, boolean>>(new Map());

  // Clear optimistic state when market selection changes
  useEffect(() => {
    setOptimistic(new Map());
    setSearch('');
    setPage(0);
  }, [selectedCustomerId]);

  // Clear optimistic entries that match server state after refetch
  useEffect(() => {
    if (!data?.users || selectedCustomerId === null) return;
    setOptimistic(prev => {
      const next = new Map(prev);
      let changed = false;
      for (const [userId, value] of prev) {
        const user = (data.users as OrgUser[]).find((u: OrgUser) => u.id === userId);
        if (!user) continue;
        const serverHasAccess = user.users_usertocustomers.some(
          utc => utc.customer_id === selectedCustomerId,
        );
        if (serverHasAccess === value) {
          next.delete(userId);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data, selectedCustomerId]);

  const hasAccess = useCallback(
    (user: OrgUser): boolean => {
      if (selectedCustomerId === null) return false;
      const opt = optimistic.get(user.id);
      if (opt !== undefined) return opt;
      return user.users_usertocustomers.some(utc => utc.customer_id === selectedCustomerId);
    },
    [optimistic, selectedCustomerId],
  );

  // Role promotion helper
  const maybeUpdateRole = useCallback(
    async (userId: number, currentRole: string, newLinkedCount: number) => {
      const targetRole = calculateTargetRole(currentRole, newLinkedCount);
      if (targetRole) {
        await updateUser({ variables: { userId, userObj: { default_role: targetRole } } });
        await syncRoles(userId);
        toast.info(`Role updated to ${getRoleLabel(targetRole)}`);
      }
    },
    [updateUser, syncRoles],
  );

  // Toggle handler
  const handleToggle = useCallback(
    async (user: OrgUser, checked: boolean) => {
      if (selectedCustomerId === null) return;

      // Only a super admin may modify a super admin's access (defense-in-depth).
      if (!canManageUser(user.default_role)) return;

      // Prevent removing your own market access
      if (!checked && user.id === currentUserId) {
        toast.warning('You cannot remove your own rooftop access');
        return;
      }

      setOptimistic(prev => new Map(prev).set(user.id, checked));

      try {
        if (checked) {
          await upsertCustomers({
            variables: {
              allowedCustomersObj: [buildCustomerUpsertObject(user.id, selectedCustomerId, currentUserEmail)],
            },
          });
        } else {
          await removeCustomer({
            variables: { userId: user.id, customerId: selectedCustomerId },
          });
        }

        const currentCount = user.users_usertocustomers.length;
        const newCount = checked ? currentCount + 1 : currentCount - 1;
        await maybeUpdateRole(user.id, user.default_role, newCount);

        refetch();
      } catch (err: any) {
        setOptimistic(prev => {
          const next = new Map(prev);
          next.delete(user.id);
          return next;
        });
        toast.error(`Failed to update rooftop access: ${err.message}`);
      }
    },
    [selectedCustomerId, currentUserId, canManageUser, currentUserEmail, upsertCustomers, removeCustomer, maybeUpdateRole, refetch],
  );

  // Sort handler
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  // Selected market info
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  return (
    <Box sx={{ display: 'flex', height: 600, border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
      {/* Left panel — Market list */}
      <Box
        sx={{
          width: '40%',
          minWidth: 240,
          borderRight: '1px solid',
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Rooftops
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            Select a rooftop to manage user access
          </Typography>
        </Box>
        <List dense disablePadding>
          {customers.map(customer => (
            <ListItemButton
              key={customer.id}
              selected={selectedCustomerId === customer.id}
              onClick={() => setSelectedCustomerId(customer.id)}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemText
                primary={customer.name}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
              />
            </ListItemButton>
          ))}
          {customers.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                No active rooftops found.
              </Typography>
            </Box>
          )}
        </List>
      </Box>

      {/* Right panel — User list with access toggles */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {selectedCustomerId === null ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Select a rooftop from the left panel.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                {selectedCustomer?.name ?? 'Rooftop'}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users by name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <BiSearch size={18} color="#778088" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading && !displayData ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : users.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>No users found.</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'display_name'}
                              direction={orderBy === 'display_name' ? order : 'asc'}
                              onClick={() => handleSort('display_name')}
                            >
                              User
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ width: 120 }}>Role</TableCell>
                          <TableCell sx={{ width: 120 }}>Rooftops</TableCell>
                          <TableCell sx={{ width: 80 }}>Access</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map(user => {
                          const access = hasAccess(user);
                          const isSelf = user.id === currentUserId;
                          const manageable = canManageUser(user.default_role);
                          return (
                            <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                              <TableCell>
                                <UserCard
                                  avatarUrl={user.avatar_url}
                                  displayName={user.display_name}
                                  email={user.email}
                                  isDriver={hasDriverRole((user as any).users_usertoroles)}
                                  driverStatus={user.driver?.status}
                                  compact
                                />
                              </TableCell>
                              <TableCell>
                                <RoleChip role={user.default_role} />
                              </TableCell>
                              <TableCell>
                                {user.users_usertocustomers.length > 0 ? (
                                  <Tooltip
                                    title={user.users_usertocustomers
                                      .map(uc => uc.customer.name)
                                      .join(', ')}
                                  >
                                    <Chip
                                      label={`${user.users_usertocustomers.length} rooftop${user.users_usertocustomers.length !== 1 ? 's' : ''}`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: 11,
                                        fontWeight: 500,
                                        backgroundColor: theme.palette.grey[100],
                                        color: theme.palette.text.secondary,
                                        border: 'none',
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>--</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {isSelf && access ? (
                                  <Tooltip title="You cannot remove your own market access">
                                    <span>
                                      <Switch size="small" checked disabled />
                                    </span>
                                  </Tooltip>
                                ) : !manageable ? (
                                  <Tooltip title="Only a super admin can change a super admin's access">
                                    <span>
                                      <Switch size="small" checked={access} disabled />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Switch
                                    size="small"
                                    checked={access}
                                    onChange={(_e, checked) => handleToggle(user, checked)}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default CustomerAccessPanel;
